import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Check, Phone, Mail, CalendarClock } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SUPPORT_TEAMS } from "@/lib/support";
import { PHONE, PHONE_HREF, EMAIL } from "@/lib/api";
import { BookCall } from "@/components/site/BookCall";

const Reach = () => (
  <div className="grid gap-4 sm:grid-cols-3">
    <a href={PHONE_HREF} data-testid="support-reach-phone" className="group rounded-3xl bg-[#0B2A6F] p-6 text-white transition-transform duration-300 hover:-translate-y-1">
      <Phone className="h-6 w-6 text-lime-300" />
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">Call</p>
      <p className="font-display text-xl font-bold">{PHONE}</p>
    </a>
    <a href={`mailto:${EMAIL}`} data-testid="support-reach-email" className="group rounded-3xl border border-[#0B2A6F]/10 bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
      <Mail className="h-6 w-6 text-emerald-600" />
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">Email</p>
      <p className="break-all font-display text-lg font-bold text-[#050B1F]">{EMAIL}</p>
    </a>
    <a href="#book-call" data-testid="support-reach-book" className="group rounded-3xl bg-lime-400 p-6 text-[#050B1F] transition-transform duration-300 hover:-translate-y-1">
      <CalendarClock className="h-6 w-6" />
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em]">Schedule</p>
      <p className="font-display text-xl font-bold">Book a call</p>
    </a>
  </div>
);

export default function SupportDetail() {
  const { slug } = useParams();
  const team = SUPPORT_TEAMS.find((t) => t.slug === slug);
  if (!team) return <Navigate to="/support" replace />;
  const others = SUPPORT_TEAMS.filter((t) => t.slug !== slug);

  return (
    <div data-testid={`support-page-${team.slug}`}>
      <PageHero eyebrow="Support" lines={team.lines} sub={team.sub} img={team.img} />

      <section className="bg-white px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal className="max-w-2xl">
            <Eyebrow>What we help with</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-[#050B1F] sm:text-5xl">How our {team.title.toLowerCase()} team <span className="text-emerald-600">has your back.</span></h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {team.help.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="group h-full rounded-3xl border border-[#0B2A6F]/10 bg-[#EAF3FF]/50 p-7 transition-colors duration-500 hover:bg-[#EAF3FF]" data-testid={`support-help-${i}`}>
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-[#0B2A6F] text-lime-300"><Check className="h-5 w-5" /></span>
                  <p className="mt-6 font-display text-xl font-extrabold tracking-tight text-[#050B1F]">{t}</p>
                  <p className="mt-2 text-sm text-slate-700">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#EAF3FF] px-4 py-24 sm:px-6 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Get in touch</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-[#050B1F] sm:text-5xl">Reach a real person.</h2>
            <p className="mt-5 max-w-md text-slate-700">Pick whatever works best for you. Your dedicated account manager is always in the loop.</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7"><Reach /></Reveal>
        </div>
      </section>

      <section className="bg-white px-4 py-24 sm:px-6 lg:px-12" data-testid="support-faq">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Eyebrow>Questions</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-[#050B1F] sm:text-5xl">{team.title} FAQ</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible defaultValue="q-0" className="space-y-3">
              {team.faqs.map(([q, a], i) => (
                <AccordionItem key={q} value={`q-${i}`} className="rounded-2xl border border-[#0B2A6F]/10 bg-[#EAF3FF]/60 px-6 data-[state=open]:bg-[#EAF3FF]">
                  <AccordionTrigger data-testid={`support-faq-trigger-${i}`} className="py-5 text-left font-display text-lg font-bold text-[#050B1F] hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="pb-5 text-slate-700">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#050B1F] px-4 py-20 text-white sm:px-6 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <Eyebrow light>More support</Eyebrow>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/support/${o.slug}`} data-testid={`support-other-${o.slug}`}
                className="group flex items-center justify-between gap-4 rounded-3xl border border-white/10 p-6 transition-colors duration-300 hover:border-lime-300 hover:bg-white/5">
                <span className="flex items-center gap-4">
                  <o.icon className="h-6 w-6 text-lime-300" />
                  <span className="font-display text-lg font-bold">{o.title}</span>
                </span>
                <ArrowUpRight className="h-5 w-5 text-lime-300 transition-transform group-hover:rotate-45" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BookCall />
    </div>
  );
}
